import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import {
  NormalizedFfsDataValues,
  useFfsStrategy,
} from './ffsStrategyContext/FfsStrategyContext'
import {
  type AllowableStressRec,
  getAllowableStressRecords,
} from './mawpSection/allowableStress/allowableStressValues'
import {
  DEFAULT_STRESS_VALUE,
  DEFAULT_TENSILE_STRENGTH,
} from './mawpSection/allowableStress/mawpDefaults'
import { computeMawpSnapshotPrefill } from './mawpSnapshotPrefill'
import { useFfsPartFourDataValues } from './part4/FfsDataValuesSection'
import { useFfsPartFiveDataValues } from './part5LevelOne/FfsDataValuesSection'
import { ValueSetterPair } from './typeUtils'
import { useOverridableBooleanParam } from './useOverridableBooleanParam'
import { useOverridableParam } from './useOverridableParam'

type MeasurementType = GeckoMeasurement | null

export type FfsSharedValuesType = ValueSetterPair<MeasurementType, 'fca'> &
  ValueSetterPair<MeasurementType, 'fcaMl'> &
  ValueSetterPair<MeasurementType, 'corrosionRate'> &
  ValueSetterPair<MeasurementType, 'corrosionRateMl'> &
  ValueSetterPair<string | null, 'targetDate'> &
  ValueSetterPair<MeasurementType, 'weldStrengthReduction'> &
  ValueSetterPair<MeasurementType, 'yieldStrength'> &
  ValueSetterPair<MeasurementType, 'tensileStrength'> &
  ValueSetterPair<MeasurementType, 'designTemp'> &
  ValueSetterPair<MeasurementType, 'designPressure'> &
  ValueSetterPair<MeasurementType, 'qualityFactor'> &
  ValueSetterPair<MeasurementType, 'pressureCoefficient'> &
  ValueSetterPair<MeasurementType, 'tsl'> &
  ValueSetterPair<MawpCalcThicknessOption, 'selectedMawpCalcThickness'> &
  ValueSetterPair<MawpCalcType | null, 'calcType'> & {
    // Read-only computed/derived values
    indicationItem: IndicationItem | undefined
    inspectionDate: string | null
    ffsDataValues: NormalizedFfsDataValues | null
    /** Allowable-stress catalog for this component's type, or null if none exists. */
    allowableStressRecords: readonly AllowableStressRec[] | null

    // Source captions for the six asset-model-prefillable operating params
    // (P, T, S, E, Y, t_sl). `undefined` when there's nothing to caption (no
    // snapshot value and no override -- i.e. showing a hardcoded default).
    designPressureCaption: string | undefined
    designTempCaption: string | undefined
    yieldStrengthCaption: string | undefined
    qualityFactorCaption: string | undefined
    pressureCoefficientCaption: string | undefined
    weldStrengthReductionCaption: string | undefined
    tslCaption: string | undefined

    // Scenario-load setters for the params `useFfsScenarios` writes to
    // (S, E, Y, W, t_sl -- P and T are never touched by a saved scenario).
    // These tag the override `source: 'scenario'` instead of `'user'`, so a
    // loaded scenario captions as "From scenario" rather than "Overridden".
    // Callers that write a real user edit must keep using the plain setters
    // above.
    setYieldStrengthFromScenario: Dispatch<SetStateAction<MeasurementType>>
    setQualityFactorFromScenario: Dispatch<SetStateAction<MeasurementType>>
    setPressureCoefficientFromScenario: Dispatch<
      SetStateAction<MeasurementType>
    >
    setWeldStrengthReductionFromScenario: Dispatch<
      SetStateAction<MeasurementType>
    >
    setTslFromScenario: Dispatch<SetStateAction<MeasurementType>>
  }

function formatInspectionDate(
  inspection: GeckoInspection | undefined
): string | null {
  if (!inspection?.data.date) return null
  const date = new Date(inspection.data.date)
  return date.toISOString().split('T')[0]
}

export type MawpCalcThicknessOption = 't_mm' | 't_am' | 't_c' | null
export type MawpCalcType = 'thinWall' | 'thickWall'

const DEFAULT_DESIGN_PRESSURE = GeckoMeasurement.CreateFromValue(
  150,
  GeckoMeasurementUnit.PoundsForcePerSqInch
)
const DEFAULT_DESIGN_TEMP = GeckoMeasurement.CreateFromValue(
  30,
  GeckoMeasurementUnit.Celsius
)
const DEFAULT_QUALITY_FACTOR = GeckoMeasurement.CreateFromValue(
  1,
  GeckoMeasurementUnit.Ratio
)
const DEFAULT_PRESSURE_COEFFICIENT = GeckoMeasurement.CreateFromValue(
  0.4,
  GeckoMeasurementUnit.Ratio
)
const DEFAULT_TSL = GeckoMeasurement.CreateFromValue(
  0,
  GeckoMeasurementUnit.Millimeters
)
const DEFAULT_WELD_STRENGTH_REDUCTION = GeckoMeasurement.CreateFromValue(
  1,
  GeckoMeasurementUnit.Ratio
)

function useCreateFfsSharedValues(
  componentModel: GeckoComponentModel | undefined,
  thicknessCdf: GeckoComponentDataFrame | undefined,
  inspection: GeckoInspection | undefined
): FfsSharedValuesType {
  const { strategy } = useFfsStrategy()
  const [fca, setFca] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Millimeters)
  )
  const [fcaMl, setFcaMl] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Millimeters)
  )
  const [corrosionRate, setCorrosionRate] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.MillimetersPerYear)
  )
  const [corrosionRateMl, setCorrosionRateMl] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.MillimetersPerYear)
  )
  const [targetDate, setTargetDate] = useState<string | null>(null)
  const inspectionDate = formatInspectionDate(inspection)
  const [tensileStrength, setTensileStrength] = useState<MeasurementType>(
    DEFAULT_TENSILE_STRENGTH
  )
  const [selectedMawpCalcThickness, setSelectedMawpCalcThickness] =
    useState<MawpCalcThicknessOption>(null)
  const [calcType, setCalcType] = useState<MawpCalcType | null>(null)

  // Base values for the six asset-model-prefillable operating params (P, T,
  // S, E, Y, t_sl), derived with `useMemo` off `componentModel` -- no effect
  // needed to "sync" a late-arriving or later-changing snapshot; see
  // `useOverridableParam`.
  const componentId = componentModel?.id
  const snapshotBase = useMemo(
    () => computeMawpSnapshotPrefill(componentModel),
    [componentModel]
  )

  const designPressureParam = useOverridableParam(
    snapshotBase.designPressure,
    DEFAULT_DESIGN_PRESSURE,
    componentId
  )
  const designTempParam = useOverridableParam(
    snapshotBase.designTemp,
    DEFAULT_DESIGN_TEMP,
    componentId
  )
  // S -- reads `allowableStress` off the snapshot (see mawpSnapshotPrefill.ts).
  const yieldStrengthParam = useOverridableParam(
    snapshotBase.allowableStress,
    DEFAULT_STRESS_VALUE,
    componentId
  )
  const qualityFactorParam = useOverridableParam(
    snapshotBase.qualityFactor,
    DEFAULT_QUALITY_FACTOR,
    componentId
  )
  const pressureCoefficientParam = useOverridableParam(
    snapshotBase.pressureCoefficient,
    DEFAULT_PRESSURE_COEFFICIENT,
    componentId
  )
  const tslParam = useOverridableParam(
    snapshotBase.tsl,
    DEFAULT_TSL,
    componentId
  )
  const weldStrengthReductionParam = useOverridableParam(
    snapshotBase.weldStrengthReduction,
    DEFAULT_WELD_STRENGTH_REDUCTION,
    componentId
  )

  const corrosionSnapshotBase = (
    componentModel as { corrosionOnInnerSurface?: boolean } | undefined
  )?.corrosionOnInnerSurface
  const corrosionParam = useOverridableBooleanParam(
    corrosionSnapshotBase,
    false,
    componentId
  )

  // Get indication data
  const { indication: indicationItem } = useIndicationData()

  // Calculate FFS data values using passed props (not state)
  const rawPartFourDataValues = useFfsPartFourDataValues({
    indicationItem,
    fca,
    fcaMl,
    componentModel,
    thicknessCdf,
    corrosionOnInnerSurface: corrosionParam.value,
    setCorrosionOnInnerSurface: corrosionParam.setValue,
  })

  const rawPartFiveDataValues = useFfsPartFiveDataValues({
    indicationItem,
    fca,
    componentModel,
    thicknessCdf,
    corrosionOnInnerSurface: corrosionParam.value,
    setCorrosionOnInnerSurface: corrosionParam.setValue,
  })

  // Normalize data values to consistent structure
  const ffsDataValues: NormalizedFfsDataValues | null = useMemo(
    () =>
      strategy.normalizeDataValues({
        partFour: rawPartFourDataValues,
        partFive: rawPartFiveDataValues,
      }),
    [strategy, rawPartFourDataValues, rawPartFiveDataValues]
  )

  // Allowable-stress catalog for this component's type (piping → B31.3; else null).
  const allowableStressRecords = useMemo(
    () => getAllowableStressRecords(componentModel?.type),
    [componentModel]
  )

  return useMemo(
    () => ({
      fca,
      setFca,
      fcaMl,
      setFcaMl,
      corrosionRate,
      setCorrosionRate,
      corrosionRateMl,
      setCorrosionRateMl,
      targetDate,
      setTargetDate,
      inspectionDate,
      weldStrengthReduction: weldStrengthReductionParam.value,
      setWeldStrengthReduction: weldStrengthReductionParam.setValue,
      setWeldStrengthReductionFromScenario:
        weldStrengthReductionParam.setFromScenario,
      weldStrengthReductionCaption: weldStrengthReductionParam.caption,
      yieldStrength: yieldStrengthParam.value,
      setYieldStrength: yieldStrengthParam.setValue,
      setYieldStrengthFromScenario: yieldStrengthParam.setFromScenario,
      yieldStrengthCaption: yieldStrengthParam.caption,
      tensileStrength,
      setTensileStrength,
      designTemp: designTempParam.value,
      setDesignTemp: designTempParam.setValue,
      designTempCaption: designTempParam.caption,
      designPressure: designPressureParam.value,
      setDesignPressure: designPressureParam.setValue,
      designPressureCaption: designPressureParam.caption,
      qualityFactor: qualityFactorParam.value,
      setQualityFactor: qualityFactorParam.setValue,
      setQualityFactorFromScenario: qualityFactorParam.setFromScenario,
      qualityFactorCaption: qualityFactorParam.caption,
      pressureCoefficient: pressureCoefficientParam.value,
      setPressureCoefficient: pressureCoefficientParam.setValue,
      setPressureCoefficientFromScenario:
        pressureCoefficientParam.setFromScenario,
      pressureCoefficientCaption: pressureCoefficientParam.caption,
      tsl: tslParam.value,
      setTsl: tslParam.setValue,
      setTslFromScenario: tslParam.setFromScenario,
      tslCaption: tslParam.caption,
      selectedMawpCalcThickness,
      setSelectedMawpCalcThickness,
      calcType,
      setCalcType,
      indicationItem,
      ffsDataValues,
      allowableStressRecords,
    }),
    [
      fca,
      fcaMl,
      corrosionRate,
      corrosionRateMl,
      targetDate,
      inspectionDate,
      weldStrengthReductionParam,
      yieldStrengthParam,
      tensileStrength,
      designTempParam,
      designPressureParam,
      qualityFactorParam,
      pressureCoefficientParam,
      tslParam,
      selectedMawpCalcThickness,
      calcType,
      indicationItem,
      ffsDataValues,
      allowableStressRecords,
    ]
  )
}

export const FfsSharedValuesContext = createContext<FfsSharedValuesType>({
  fca: null,
  setFca: () => {},
  fcaMl: null,
  setFcaMl: () => {},
  corrosionRate: null,
  setCorrosionRate: () => {},
  corrosionRateMl: null,
  setCorrosionRateMl: () => {},
  targetDate: null,
  setTargetDate: () => {},
  inspectionDate: null,
  weldStrengthReduction: null,
  setWeldStrengthReduction: () => {},
  setWeldStrengthReductionFromScenario: () => {},
  weldStrengthReductionCaption: undefined,
  yieldStrength: null,
  setYieldStrength: () => {},
  setYieldStrengthFromScenario: () => {},
  yieldStrengthCaption: undefined,
  tensileStrength: null,
  setTensileStrength: () => {},
  designTemp: null,
  setDesignTemp: () => {},
  designTempCaption: undefined,
  designPressure: null,
  setDesignPressure: () => {},
  designPressureCaption: undefined,
  qualityFactor: null,
  setQualityFactor: () => {},
  setQualityFactorFromScenario: () => {},
  qualityFactorCaption: undefined,
  pressureCoefficient: null,
  setPressureCoefficient: () => {},
  setPressureCoefficientFromScenario: () => {},
  pressureCoefficientCaption: undefined,
  tsl: null,
  setTsl: () => {},
  setTslFromScenario: () => {},
  tslCaption: undefined,
  selectedMawpCalcThickness: null,
  setSelectedMawpCalcThickness: () => {},
  calcType: null,
  setCalcType: () => {},
  indicationItem: undefined,
  ffsDataValues: null,
  allowableStressRecords: null,
})

export interface FfsSharedValueProviderProps {
  children: ReactNode
  componentModel: GeckoComponentModel | undefined
  thicknessCdf: GeckoComponentDataFrame | undefined
  inspection: GeckoInspection | undefined
}

export const FfsSharedValueProvider: FC<FfsSharedValueProviderProps> = ({
  children,
  componentModel,
  thicknessCdf,
  inspection,
}) => {
  return (
    <FfsSharedValuesContext.Provider
      value={useCreateFfsSharedValues(
        componentModel,
        thicknessCdf,
        inspection
      )}>
      {children}
    </FfsSharedValuesContext.Provider>
  )
}
